import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MapPin,
  Clock,
  Users,
  AlertTriangle,
  Calendar,
  Navigation,
  Target,
  CheckCircle,
  XCircle,
  Download,
  Eye,
  Phone,
  MessageSquare,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Filter,
  LayoutDashboard
} from 'lucide-react';
import { toast } from 'react-hot-toast';
// import Button from '../../../components/common/Button/Button';
import { handleTextInputChange, getCharacterCount, FIELD_LIMITS } from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, generateExportFilename } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const FieldSalesDashboard = () => {
  const navigate = useNavigate();
  const [selectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showGeoMapModal, setShowGeoMapModal] = useState(false);
  const [showRoutePlansModal, setShowRoutePlansModal] = useState(false);
  const [showSyncSettingsModal, setShowSyncSettingsModal] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showVisitDetailsModal, setShowVisitDetailsModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);

  // Sorting state for recent visits
  const [sortColumn, setSortColumn] = useState('time');
  const [sortDirection, setSortDirection] = useState('desc');

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Date Filter state
  const [selectedPeriod, setSelectedPeriod] = useState('last_30_days');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const dashboardFilters = React.useMemo(() => {
    const f = {};
    if (selectedPeriod === 'custom' || customDateRange.startDate || customDateRange.endDate) {
      if (customDateRange.startDate) f.startDate = customDateRange.startDate;
      if (customDateRange.endDate) f.endDate = customDateRange.endDate;
      f.range = 'custom';
    } else {
      f.range = selectedPeriod;
    }
    return f;
  }, [selectedPeriod, customDateRange]);

  // React Query: Fetch aggregated dashboard statistics
  const {
    data: statsData,
    isLoading: loadingStats,
    isError: statsError,
    refetch: refetchStats
  } = useQuery({
    queryKey: ['fieldSales', 'dashboardStats', dashboardFilters],
    queryFn: async () => {
      const response = await fieldSalesService.getDashboardStats(dashboardFilters);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load dashboard statistics');
      }
    },
    onError: () => {
    }
  });

  // Extract data from aggregated stats
  const todayStats = statsData?.kpis || {
    totalVisits: 0,
    completedVisits: 0,
    scheduledVisits: 0,
    cancelledVisits: 0,
    totalDistance: 0,
    inProcessVisits: 0
  };

  const reps = statsData?.teamPerformance || [];
  const recentVisits = statsData?.recentVisits || [];

  // Format time from 24-hour to 12-hour format
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;
      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];
      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Format status to human-readable format
  const formatStatus = (status) => {
    if (!status) return 'Scheduled';
    if (status === 'completed') return 'Visited';
    return status
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format duration in minutes to Xh Ym
  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const handleViewGeoMap = () => {
    setShowGeoMapModal(true);
  };

  const handleViewRoutePlans = () => {
    setShowRoutePlansModal(true);
  };

  const handleViewVisitLog = () => {
    navigate('/route_plans');
  };

  const handleViewSyncSettings = () => {
    setShowSyncSettingsModal(true);
  };


  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleExportCurrentPage = () => {
    try {
      const dataToExport = getSortedRecentVisits();

      const headers = ['Customer', 'Assigned To', 'Visit Date', 'Visit Time', 'Status', 'Duration'];

      const formattedData = dataToExport.map(visit => ({
        'Customer': visit.customer || '',
        'Assigned To': visit.rep || '',
        'Visit Date': visit.date ? formatDate(visit.date, { includeTime: false }) : '',
        'Visit Time': formatTimeTo12Hour(visit.time),
        'Status': formatStatus(visit.status || 'scheduled'),
        'Duration': formatDuration(visit.duration || 0)
      }));

      exportToCSV(formattedData, 'field_sales_visits', { title: 'Field Sales Visits', type: 'Current Page' }, headers);
      toast.success('Visits exported successfully');
    } catch (error) {
      toast.error('Failed to export visits');
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const dataToExport = getSortedRecentVisits();
      const headers = ['Customer', 'Assigned To', 'Visit Date', 'Visit Time', 'Status', 'Duration'];

      const formattedData = dataToExport.map(visit => ({
        'Customer': visit.customer || '',
        'Assigned To': visit.rep || '',
        'Visit Date': visit.date ? formatDate(visit.date, { includeTime: false }) : '',
        'Visit Time': formatTimeTo12Hour(visit.time),
        'Status': formatStatus(visit.status || 'scheduled'),
        'Duration': formatDuration(visit.duration || 0)
      }));

      exportToCSV(formattedData, 'field_sales_visits_all', { title: 'Field Sales Visits', type: 'All Data' }, headers);
      toast.success('All visits exported successfully');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export visits');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const dataToExport = getSortedRecentVisits();

      const headers = ['Customer', 'Assigned To', 'Visit Date', 'Visit Time', 'Status', 'Duration'];

      const formattedData = dataToExport.map(visit => ({
        'Customer': visit.customer || '',
        'Assigned To': visit.rep || '',
        'Visit Date': visit.date ? formatDate(visit.date, { includeTime: false }) : '',
        'Visit Time': formatTimeTo12Hour(visit.time),
        'Status': formatStatus(visit.status || 'scheduled'),
        'Duration': formatDuration(visit.duration || 0)
      }));

      const type = `Date Range (${startDate} to ${endDate})`;
      exportToCSV(formattedData, 'field_sales_visits_range', { title: 'Field Sales Visits', type }, headers);
      toast.success('Date range export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export visits');
    } finally {
      setExportLoading(false);
    }
  };



  const handleCallCustomer = (customer) => {
    // Simulate calling customer
    toast.loading(`Calling ${customer.name}...`);
    setTimeout(() => {
      toast.dismiss();
      toast.success(`Call to ${customer.name} completed`);
    }, 2000);
  };

  const handleOptimizeRoutes = async () => {
    setIsOptimizing(true);
    toast.loading('Optimizing routes...');

    // Simulate route optimization
    await new Promise(resolve => setTimeout(resolve, 3000));

    setIsOptimizing(false);
    toast.dismiss();
    toast.success('Routes optimized successfully! Time saved: 2.5 hours');
  };


  const handleSaveSettings = async () => {
    setIsSaving(true);
    toast.loading('Saving sync settings...');

    // Simulate save process
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSaving(false);
    toast.dismiss();
    toast.success('Sync settings saved successfully!');
    setShowSyncSettingsModal(false);
  };

  const handleViewVisitDetails = (visit) => {
    setSelectedVisit(visit);
    setShowVisitDetailsModal(true);
  };

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;

    const newMessage = {
      id: chatMessages.length + 1,
      sender: 'You',
      message: chatMessage,
      timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
      type: 'user'
    };

    setChatMessages([...chatMessages, newMessage]);
    setChatMessage('');

    // Simulate response from rep
    setTimeout(() => {
      const responseMessage = {
        id: chatMessages.length + 2,
        sender: selectedVisit.rep,
        message: 'Thanks for the message! I\'ll get back to you shortly.',
        timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
        type: 'rep'
      };
      setChatMessages(prev => [...prev, responseMessage]);
    }, 1000);
  };

  const handleCloseVisitDetails = () => {
    setShowVisitDetailsModal(false);
    setSelectedVisit(null);
  };


  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted recent visits for the table
  const getSortedRecentVisits = () => {
    if (!sortColumn || !recentVisits || recentVisits.length === 0) return recentVisits;

    return [...recentVisits].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'customer') {
        aValue = (a.customer || '').toLowerCase();
        bValue = (b.customer || '').toLowerCase();
      } else if (sortColumn === 'rep') {
        aValue = (a.rep || '').toLowerCase();
        bValue = (b.rep || '').toLowerCase();
      } else if (sortColumn === 'date') {
        aValue = a.date || '';
        bValue = b.date || '';
      } else if (sortColumn === 'time') {
        aValue = a.time || '';
        bValue = b.time || '';
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'duration') {
        aValue = Number(a.duration) || 0;
        bValue = Number(b.duration) || 0;
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  function getStatusColor(status) {
    switch (status) {
      case 'completed': return 'text-teal-700 bg-teal-50';
      case 'in-progress':
      case 'in-process': return 'text-[#0F2942] bg-blue-50';
      case 'scheduled': return 'text-gray-600 bg-gray-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 !mt-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <LayoutDashboard className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Field Sales Dashboard</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Monitor field sales activities and performance</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={selectedPeriod}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedPeriod(val);
              if (val !== 'custom') {
                setCustomDateRange({ startDate: '', endDate: '' });
              }
            }}
            className="input-field w-full sm:w-auto cursor-pointer"
          >
            <option value="all_time">All Time</option>
            <option value="today">Today</option>
            <option value="last_7_days">Last 7 days</option>
            <option value="last_30_days">Last 30 days</option>
            <option value="last_60_days">Last 60 days</option>
            <option value="last_90_days">Last 90 days</option>
            <option value="last_year">Last year</option>
            <option value="custom">Custom Date</option>
          </select>

          {(selectedPeriod === 'custom' || customDateRange.startDate || customDateRange.endDate) && (
            <DateRangeFilter
              startDate={customDateRange.startDate}
              endDate={customDateRange.endDate}
              onChange={(range) => {
                setCustomDateRange(range);
                if (range.startDate || range.endDate) {
                  setSelectedPeriod('custom');
                }
              }}
              allowFutureDates={false}
            />
          )}

          {(selectedPeriod === 'custom' || customDateRange.startDate || customDateRange.endDate) && (
            <button
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium h-8 cursor-pointer"
              onClick={() => {
                setCustomDateRange({ startDate: '', endDate: '' });
                setSelectedPeriod('last_30_days');
              }}
            >
              Clear
            </button>
          )}
          <PermissionGate permission="field_sales" action="read">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleExportClick}
            >
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Loading State */}
      {loadingStats && (
        <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard statistics...</p>
        </div>
      )}

      {/* Error State */}
      {statsError && (
        <div className="bg-white p-8 rounded-lg border border-red-200 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">Failed to load dashboard statistics. Please try again.</p>
          <button
            className="btn-cancel mt-4"
            onClick={() => refetchStats()}
          >
            Retry
          </button>
        </div>
      )}

      {/* Dashboard Content - Only show when data is loaded */}
      {!loadingStats && !statsError && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* 1. Total Visits */}
            <div className="bg-white p-5 rounded-2xl border border-blue-200/80 shadow-2xs hover:shadow-md hover:border-blue-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-blue-600 transition-colors">Total Visits</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 group-hover:text-blue-900 transition-colors">{formatIndianNumber(todayStats.totalVisits)}</p>
                </div>
                <div className="w-11 h-11 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 border border-blue-100 group-hover:scale-110 transition-transform">
                  <Calendar className="h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 2. Completed */}
            <div className="bg-white p-5 rounded-2xl border border-emerald-200/80 shadow-2xs hover:shadow-md hover:border-emerald-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-emerald-600 transition-colors">Completed</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 group-hover:text-emerald-900 transition-colors">{formatIndianNumber(todayStats.completedVisits)}</p>
                </div>
                <div className="w-11 h-11 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center flex-shrink-0 border border-emerald-100 group-hover:scale-110 transition-transform">
                  <CheckCircle className="h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 3. Scheduled */}
            <div className="bg-white p-5 rounded-2xl border border-amber-200/80 shadow-2xs hover:shadow-md hover:border-amber-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-amber-600 transition-colors">Scheduled</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 group-hover:text-amber-900 transition-colors">{formatIndianNumber(todayStats.scheduledVisits || 0)}</p>
                </div>
                <div className="w-11 h-11 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center flex-shrink-0 border border-amber-100 group-hover:scale-110 transition-transform">
                  <Clock className="h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 4. In Process */}
            <div className="bg-white p-5 rounded-2xl border border-cyan-200/80 shadow-2xs hover:shadow-md hover:border-cyan-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-cyan-600 transition-colors">In Process</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 group-hover:text-cyan-900 transition-colors">{formatIndianNumber(todayStats.inProcessVisits || 0)}</p>
                </div>
                <div className="w-11 h-11 bg-cyan-50 text-cyan-600 rounded-full flex items-center justify-center flex-shrink-0 border border-cyan-100 group-hover:scale-110 transition-transform">
                  <Navigation className="h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 5. Cancelled */}
            <div className="bg-white p-5 rounded-2xl border border-rose-200/80 shadow-2xs hover:shadow-md hover:border-rose-400 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 cursor-pointer group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-rose-600 transition-colors">Cancelled</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1.5 group-hover:text-rose-900 transition-colors">{formatIndianNumber(todayStats.cancelledVisits || 0)}</p>
                </div>
                <div className="w-11 h-11 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center flex-shrink-0 border border-rose-100 group-hover:scale-110 transition-transform">
                  <XCircle className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Team Performance */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-2xs">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Performance</h2>
              </div>
              <div className="space-y-4">
                {reps.length > 0 ? (
                  reps.map(rep => (
                    <div key={rep.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-[#0F2942]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{rep.name}</p>
                          <p className="text-sm text-gray-600">{rep.visits} visits planned</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{rep.completed}/{rep.visits}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No performance data available.</p>
                    <p className="text-sm text-gray-500 mt-1">Performance metrics will appear here once visits are assigned.</p>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Recent Visits */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-2xs">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Visits</h2>
              <button
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center h-8"
                onClick={handleViewVisitLog}
              >
                View All
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Customer</span>
                        {sortColumn === 'customer' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('rep')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Assigned To</span>
                        {sortColumn === 'rep' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Visit Date</span>
                        {sortColumn === 'date' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === 'status' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('duration')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Duration</span>
                        {sortColumn === 'duration' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentVisits.length > 0 ? (
                    getSortedRecentVisits().map(visit => (
                      <tr key={visit.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize">
                          <div className="max-w-xs truncate" title={visit.customer}>
                            {visit.customer}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                          {visit.rep}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{visit.date ? formatDate(visit.date, { includeTime: false }) : 'N/A'}</span>
                            <span className="text-xs text-gray-500">{formatTimeTo12Hour(visit.time)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(visit.status)}`}>
                            {formatStatus(visit.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDuration(visit.duration)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            className="text-[#0F2942] hover:text-blue-900 px-2 py-1 rounded border border-[#0F2942] hover:bg-blue-50 transition-colors"
                            onClick={() => navigate(`/route_plan_details/${visit.routePlanId}`)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center">
                        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No recent visits recorded.</p>
                        <p className="text-sm text-gray-500 mt-1">Visit logs will appear here once visits are completed.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )
      }

      {/* Geo Map Modal */}
      {
        showGeoMapModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Geographic Map</h3>
                <button
                  onClick={() => setShowGeoMapModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-[#0F2942]" />
                    <h4 className="text-lg font-medium text-blue-900">Field Sales Locations</h4>
                  </div>
                  <p className="text-[#0B2033] mt-2">View real-time locations of your field sales team and customer visits.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Active Assignees</h5>
                    <p className="text-2xl font-bold text-[#0F2942]">{formatIndianNumber(reps.length)}</p>
                    <p className="text-sm text-gray-600">Currently in field</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Today's Visits</h5>
                    <p className="text-2xl font-bold text-green-600">{formatIndianNumber(todayStats.totalVisits)}</p>
                    <p className="text-sm text-gray-600">Scheduled visits</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-2">Distance Covered</h5>
                    <p className="text-2xl font-bold text-purple-600">{formatIndianNumber(todayStats.totalDistance)} km</p>
                    <p className="text-sm text-gray-600">Total distance</p>
                  </div>
                </div>

                <div className="bg-gray-100 border border-gray-200 rounded-lg p-8 text-center">
                  <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Interactive Map View</h4>
                  <p className="text-gray-600 mb-4">This would display an interactive map showing:</p>
                  <ul className="text-left text-sm text-gray-600 space-y-1 max-w-md mx-auto">
                    <li>• Real-time location of assignees</li>
                    <li>• Customer visit locations and status</li>
                    <li>• Route optimization suggestions</li>
                    <li>• Geographic performance analytics</li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => setShowGeoMapModal(false)}
                >
                  Close
                </button>
                <button
                  className="btn-create flex items-center justify-center space-x-2"
                >
                  <MapPin className="h-4 w-4" />
                  <span>Open Full Map</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Route Plans Modal */}
      {
        showRoutePlansModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Route Plans</h3>
                <button
                  onClick={() => setShowRoutePlansModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-green-600" />
                    <h4 className="text-lg font-medium text-green-900">Optimized Route Planning</h4>
                  </div>
                  <p className="text-green-700 mt-2">AI-powered route optimization for maximum efficiency and customer coverage.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Today's Routes</h5>
                    {reps.map(rep => (
                      <div key={rep.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-gray-900">{rep.name}</h6>
                          <span className="text-sm text-gray-500">{rep.visits} visits</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>8:00 AM - 5:00 PM</span>
                        </div>
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-600 h-2 rounded-full" style={{ width: `${(rep.completed / rep.visits) * 100}%` }}></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{rep.completed}/{rep.visits} completed</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Route Optimization</h5>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Total Distance</span>
                        <span className="text-sm font-bold text-gray-900">{todayStats.totalDistance} km</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Time Saved</span>
                        <span className="text-sm font-bold text-green-600">2.5 hours</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Fuel Efficiency</span>
                        <span className="text-sm font-bold text-[#0F2942]">+15%</span>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h6 className="font-medium text-blue-900 mb-2">Optimization Suggestions</h6>
                      <ul className="text-sm text-[#0B2033] space-y-1">
                        <li>• Combine visits in North Zone</li>
                        <li>• Reschedule afternoon appointments</li>
                        <li>• Use express route for urgent visits</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => setShowRoutePlansModal(false)}
                >
                  Close
                </button>
                <button
                  className="btn-create flex items-center justify-center space-x-2"
                  onClick={handleOptimizeRoutes}
                  disabled={isOptimizing}
                >
                  {isOptimizing ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <Target className="h-4 w-4 mr-2" />}
                  <span>{isOptimizing ? 'Optimizing...' : 'Optimize Routes'}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }


      {/* Sync Settings Modal */}
      {
        showSyncSettingsModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Sync Settings</h3>
                <button
                  onClick={() => setShowSyncSettingsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Navigation className="h-5 w-5 text-orange-600" />
                    <h4 className="text-lg font-medium text-orange-900">Data Synchronization</h4>
                  </div>
                  <p className="text-orange-700 mt-2">Configure how your field sales data syncs with the central system.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Sync Frequency</h5>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="radio" name="syncFrequency" value="realtime" className="mr-3" defaultChecked />
                        <span className="text-sm text-gray-700">Real-time sync</span>
                      </label>
                      <label className="flex items-center">
                        <input type="radio" name="syncFrequency" value="5min" className="mr-3" />
                        <span className="text-sm text-gray-700">Every 5 minutes</span>
                      </label>
                      <label className="flex items-center">
                        <input type="radio" name="syncFrequency" value="15min" className="mr-3" />
                        <span className="text-sm text-gray-700">Every 15 minutes</span>
                      </label>
                      <label className="flex items-center">
                        <input type="radio" name="syncFrequency" value="hourly" className="mr-3" />
                        <span className="text-sm text-gray-700">Hourly</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Sync Status</h5>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-900">Connected</span>
                      </div>
                      <p className="text-sm text-green-700">Last sync: 2 minutes ago</p>
                      <p className="text-sm text-green-700">Next sync: In 3 minutes</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900">Data Types</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700">Visit records</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700">Customer data</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700">Location data</span>
                      </label>
                    </div>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700">Sales activities</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700">Route plans</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-3" />
                        <span className="text-sm text-gray-700">Offline mode data</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h6 className="font-medium text-blue-900 mb-2">Sync Statistics</h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-[#0B2033]">Records synced today</p>
                      <p className="font-bold text-blue-900">1,247</p>
                    </div>
                    <div>
                      <p className="text-[#0B2033]">Sync success rate</p>
                      <p className="font-bold text-blue-900">99.8%</p>
                    </div>
                    <div>
                      <p className="text-[#0B2033]">Data size</p>
                      <p className="font-bold text-blue-900">2.3 MB</p>
                    </div>
                    <div>
                      <p className="text-[#0B2033]">Sync time</p>
                      <p className="font-bold text-blue-900">1.2s</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => setShowSyncSettingsModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-create flex items-center justify-center space-x-2"
                  onClick={handleSaveSettings}
                  disabled={isSaving}
                >
                  {isSaving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <CheckCircle className="h-4 w-4 mr-2" />}
                  <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }


      {/* Visit Details Modal */}
      {
        showVisitDetailsModal && selectedVisit && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Visit Details</h3>
                <button
                  onClick={handleCloseVisitDetails}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedVisit.customer}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                      <p className="text-sm text-gray-900 capitalize">{selectedVisit.rep}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
                      <p className="text-sm text-gray-900">{selectedDate} at {selectedVisit.time}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedVisit.status)}`}>
                        {selectedVisit.status}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                      <p className="text-sm text-gray-900">{selectedVisit.duration}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Visit ID</label>
                      <p className="text-sm text-gray-900">{selectedVisit.id}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visit Notes</label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      {selectedVisit.status === 'completed'
                        ? 'Visit completed successfully. Customer showed interest in our new product line. Follow-up scheduled for next week.'
                        : selectedVisit.status === 'in-progress'
                          ? 'Visit currently in progress. Assignee is discussing product features with the customer.'
                          : 'Visit scheduled. Assignee will arrive at the scheduled time.'
                      }
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Next Actions</label>
                  <div className="space-y-2">
                    {selectedVisit.status === 'completed' && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Follow-up call scheduled</span>
                      </div>
                    )}
                    {selectedVisit.status === 'in-progress' && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-[#0F2942]" />
                        <span>Visit in progress</span>
                      </div>
                    )}
                    {selectedVisit.status === 'scheduled' && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <span>Awaiting visit start</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={handleCloseVisitDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export Field Sales Visits"
        showDateRange={true}
      />
    </div >
  );
};

export default FieldSalesDashboard;
